/**
 * Shared traversal helpers for the FilterBar's `PropertyFilterGroup`
 * tree. Every feature that rewrites the state on a UI click (Status
 * Overview segment click, Step Breakdown pivot-cell click, FilterBar
 * drill-down chips) uses these so the call sites can't diverge on
 * the shape of the walk.
 *
 * All helpers recurse fully through nested `PropertyFilterGroup`s.
 * Shared / hand-crafted / legacy URLs parse into multiple sibling
 * groups at the root — e.g.
 * `(and (and (range …)) (or (filter productLineId …) …) (and
 * (filter programId …)))` becomes three sibling PFGs — and
 * `parsePropertyFilterFromQueryString` accepts up to three levels
 * of nesting, so chips can live at any depth. Any helper that
 * shortcuts to `filters.find(f => f.type === "PropertyFilterGroup")`
 * silently discards every sibling but the first.
 */

import type {
  PropertyFilter,
  PropertyFilterGroup,
} from "./searchSideBarConnector";

/**
 * Returns a fresh `PropertyFilterGroup` with every leaf that
 * satisfies `shouldDrop` removed. `group` is never mutated. Each
 * surviving `PropertyFilterGroup` keeps its `operation` — critical
 * for multi-value OR groups (e.g. `or(productLineId=A,
 * productLineId=B)`), which rewriting to `and` would silently reduce
 * to zero matches.
 *
 * Nested groups that empty out are dropped so the tree doesn't
 * accumulate empty subgroups on repeated toggles.
 */
export function filterPropertyFilterTree(
  group: PropertyFilterGroup,
  shouldDrop: (filter: PropertyFilter) => boolean
): PropertyFilterGroup {
  const nextChildren: Array<PropertyFilter | PropertyFilterGroup> = [];
  for (const child of group.filters) {
    if (child.type === "PropertyFilter") {
      if (!shouldDrop(child)) {
        nextChildren.push(child);
      }
      continue;
    }
    const strippedChild = filterPropertyFilterTree(child, shouldDrop);
    if (strippedChild.filters.length > 0) {
      nextChildren.push(strippedChild);
    }
  }
  return {
    type: "PropertyFilterGroup",
    operation: group.operation,
    filters: nextChildren,
  };
}

/**
 * `true` iff any leaf `PropertyFilter` in the tree satisfies
 * `predicate`. Recurses through every nested group.
 */
export function someLeafFilter(
  group: PropertyFilterGroup,
  predicate: (filter: PropertyFilter) => boolean
): boolean {
  return group.filters.some((f) =>
    f.type === "PropertyFilter" ? predicate(f) : someLeafFilter(f, predicate)
  );
}

/**
 * Visit every leaf `PropertyFilter` in the tree, at any depth. The
 * visit order is a depth-first pre-order walk; visitors that care
 * about ordering (e.g. building an ordered list of active values)
 * can rely on that.
 */
export function forEachLeafFilter(
  group: PropertyFilterGroup,
  visit: (filter: PropertyFilter) => void
): void {
  for (const child of group.filters) {
    if (child.type === "PropertyFilter") {
      visit(child);
    } else {
      forEachLeafFilter(child, visit);
    }
  }
}
